import { formattedBrandName } from 'AppBranding';
import { type Cohort, getBranding, getSupportEmailAddress } from 'Cohorts';
import { getProgramConfigValue } from 'Program';
import {
    type CurrentUser,
    getAoiRecord,
    getCohort,
    getHasFailedProgram,
    getHasPendingAdmissionOfferOrIsNotYetCurrent,
    getIsCurrentOrHasCompletedProgram,
} from 'Users';
import { type ApplicationStatusMessages } from './applicationStatus.types';

/*
    Builds the Program Status messaging. There is no pending, rejected or expelled messaging, because
    no such learner reaches this screen. https://github.com/pedago/back_royal/pull/13560 stopped
    showing the section to any user whose active AOI record is a program application. A declined offer
    or an expelled inclusion fails `getHasOfferOrIncludedOrCompleted`, so the nav hides the tab and
    settings_dir.js sends a direct link to the Account section. See https://trello.com/c/4pcyVnov.
*/
export function getApplicationStatusMessages(currentUser: CurrentUser): ApplicationStatusMessages {
    const cohort = getCohort(currentUser);
    const lastActiveAoiRecord = getAoiRecord(currentUser);

    if (!cohort || !lastActiveAoiRecord) return { acceptedPrimary: null };

    const acceptedPrimaryKey = getAcceptedPrimaryKey(currentUser, cohort);
    if (!acceptedPrimaryKey) return { acceptedPrimary: null };

    return {
        acceptedPrimary: {
            key: `settings.application_status.${acceptedPrimaryKey}`,
            values: {
                programTitle: getProgramConfigValue<string>(cohort.programType, 'shortProgramTitle') ?? '',
                brandName: formattedBrandName('short', getBranding(cohort)),
                acceptedPrimaryMessageDefaultBrandName:
                    getProgramConfigValue<string | null>(
                        cohort.programType,
                        'applicationStatusAcceptedPrimaryMessageDefaultBrandName',
                    ) ?? '',
            },
            brandEmail: getSupportEmailAddress(cohort, 'support') ?? '',
        },
    };
}

function getAcceptedPrimaryKey(currentUser: CurrentUser, cohort: Cohort): string | null {
    if (getHasPendingAdmissionOfferOrIsNotYetCurrent(currentUser)) return 'accepted_primary_message_default';

    if (getIsCurrentOrHasCompletedProgram(currentUser)) {
        return getHasFailedProgram(currentUser)
            ? 'failed_unfortunately'
            : getProgramConfigValue<string | null>(cohort.programType, 'applicationStatusAcceptedPrimaryMessageKey');
    }

    return null;
}
